import { useEffect, useState } from "react";
import { Robot, ArrowsClockwise, Key, Trash, Copy, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STATE_META = {
  connected: { variant: "default", label: "Connected" },
  busy: { variant: "secondary", label: "Busy" },
  error: { variant: "destructive", label: "Session unavailable" },
  disconnected: { variant: "outline", label: "Disconnected" },
};

// Dedicated AI Studio card for the global "ChatGPT Browser" provider (SPRINT-PH-15 Phase E).
// Shows live bridge state, ChatGPT session availability, a real Test Provider action, a bridge
// token setup and diagnostics — NO API key configuration (it uses the browser session only).
export function ChatGptBridgeCard({ provider }) {
  const [status, setStatus] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [newToken, setNewToken] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    api.get("/ai/bridge/chatgpt/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/ai/bridge/chatgpt/token").then((r) => setTokenInfo(r.data)).catch(() => {});
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000);
    return () => clearInterval(t);
  }, []);

  const genToken = async () => {
    if (tokenInfo?.configured &&
        !window.confirm("Rotate the bridge token? Any connected extension will stop working until you paste the new token.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/bridge/chatgpt/token");
      setNewToken(data.token);
      toast.success("Bridge token generated — copy it into the extension now");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const revokeToken = async () => {
    if (!window.confirm("Revoke the bridge token? The extension will disconnect immediately.")) return;
    setBusy(true);
    try {
      await api.delete("/ai/bridge/chatgpt/token");
      setNewToken("");
      toast.success("Bridge token revoked");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/ai/bridge/chatgpt/test");
      setTestResult(data);
      if (data.ok && data.matched) toast.success("ChatGPT Browser responded correctly");
      else if (data.ok) toast.warning("Responded, but with unexpected text");
      else toast.error(data.error || "ChatGPT Browser test failed");
    } catch (e) { toast.error(apiError(e)); }
    setTesting(false);
    refresh();
  };

  const st = status?.state || "disconnected";
  const meta = STATE_META[st] || STATE_META.disconnected;

  return (
    <div className="bg-card border border-border rounded-sm p-4 space-y-3 sm:col-span-2 lg:col-span-3"
      data-testid="ai-provider-card-chatgpt_browser">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Robot size={20} className="text-primary" />
          <div>
            <p className="font-medium">{provider?.name || "ChatGPT Browser"}</p>
            <p className="text-[10px] font-mono text-muted-foreground uppercase">browser bridge · no API key</p>
          </div>
        </div>
        <Badge variant={meta.variant} className="rounded-sm text-[10px] uppercase" data-testid="chatgpt-bridge-state">
          {meta.label}
        </Badge>
      </div>

      <p className="text-[11px] text-muted-foreground">
        Zero-API-cost text provider that routes through your authenticated ChatGPT browser session
        via the GrowthAI ChatGPT Bridge extension. Your ChatGPT session never leaves your browser.
      </p>

      {/* Diagnostics */}
      <div className="grid grid-cols-2 gap-2 text-[11px]" data-testid="chatgpt-bridge-diagnostics">
        <div className="rounded-sm border border-border bg-background/40 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Bridge</p>
          <p>{status?.connected ? "online" : "offline"}</p>
        </div>
        <div className="rounded-sm border border-border bg-background/40 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wide text-muted-foreground">ChatGPT session</p>
          <p>{status?.session_available ? "available" : "unavailable"}</p>
        </div>
        <div className="rounded-sm border border-border bg-background/40 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Last seen</p>
          <p>{status?.last_seen ? new Date(status.last_seen).toLocaleTimeString() + (status?.connected ? "" : " · stale") : "—"}</p>
        </div>
        <div className="rounded-sm border border-border bg-background/40 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Pending jobs</p>
          <p>{status?.pending_jobs ?? 0}</p>
        </div>
        <div className="rounded-sm border border-border bg-background/40 px-2.5 py-1.5">
          <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Extension</p>
          <p data-testid="chatgpt-bridge-ext-version">{status?.version ? `v${status.version}` : "—"}</p>
        </div>
      </div>

      {/* Bridge token setup */}
      <div className="space-y-2 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground">
            Bridge token: {tokenInfo?.configured
              ? <span className="font-mono text-foreground">{tokenInfo.prefix}…</span>
              : <span>not created</span>}
          </p>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" disabled={busy}
              onClick={genToken} data-testid="chatgpt-bridge-gen-token">
              <Key size={13} className="mr-1" />{tokenInfo?.configured ? "Rotate" : "Generate"}
            </Button>
            {tokenInfo?.configured && (
              <Button size="sm" variant="ghost" className="rounded-sm h-7 text-[11px]" disabled={busy}
                onClick={revokeToken} title="Revoke bridge token" aria-label="Revoke bridge token"
                data-testid="chatgpt-bridge-revoke-token">
                <Trash size={13} />
              </Button>
            )}
          </div>
        </div>
        {newToken && (
          <div className="rounded-sm bg-muted/60 border border-border p-2 flex items-center gap-2" data-testid="chatgpt-bridge-token-value">
            <code className="text-[10px] break-all flex-1">{newToken}</code>
            <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0"
              onClick={() => { navigator.clipboard?.writeText(newToken); toast.success("Copied"); }}>
              <Copy size={13} />
            </Button>
          </div>
        )}
        {newToken && <p className="text-[10px] text-amber-500">Copy this now — it is shown only once.</p>}
      </div>

      {/* Test Provider */}
      <div className="flex items-center gap-2 border-t border-border pt-3">
        <Button size="sm" className="rounded-sm h-8 text-xs" disabled={testing}
          onClick={runTest} data-testid="chatgpt-bridge-test">
          <ArrowsClockwise size={14} className={`mr-1 ${testing ? "animate-spin" : ""}`} />
          {testing ? "Testing…" : "Test Provider"}
        </Button>
        {testResult && (
          <span className="text-[11px] flex items-center gap-1" data-testid="chatgpt-bridge-test-result">
            {testResult.ok
              ? <><CheckCircle size={14} className="text-primary" />{testResult.matched ? "OK" : "Unexpected"} · {testResult.text?.slice(0, 40)}</>
              : <><WarningCircle size={14} className="text-destructive" />{testResult.error}</>}
          </span>
        )}
      </div>
    </div>
  );
}
